import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import * as c from "./useModal.module.scss";
import { IconClose } from "@components/icons/iconClose";
import { useScrollLock } from "@hooks/useScrollLock";

export const Modal = ({ isOpen, onClose, className, closeButtonClassName, children }) => {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const [isClosing, setIsClosing] = useState(false);
  const [prevIsOpen, setPrevIsOpen] = useState(false);

  if (isOpen !== prevIsOpen) {
    setPrevIsOpen(isOpen);
    if (prevIsOpen && !isOpen) setIsClosing(true);
    if (isOpen) setIsClosing(false);
  }

  const isVisible = isOpen || isClosing;

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  useScrollLock(isVisible);

  if (!isVisible) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={classNames(c.overlay, isClosing && c.closing)}
      onClick={(e) => {
        if (modalRef.current && !modalRef.current.contains(e.target)) {
          onClose();
        }
      }}
      onAnimationEnd={(e) => {
        if (isClosing && e.target === overlayRef.current) {
          setIsClosing(false);
        }
      }}
    >
      <div ref={modalRef} className={classNames(c.modal, className)}>
        <button type="button" className={classNames(c.closeButton, closeButtonClassName)} onClick={onClose}>
          <IconClose />
        </button>
        {children}
      </div>
    </div>,
    document.body
  );
};

export const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  return { isOpen, openModal, closeModal };
};

export default useModal;
